import { Component, DestroyRef, effect, inject, Injector, input, OnInit } from '@angular/core';
import { OrgAdminEndpointsService } from '../../../../services/org-service/org-admin-endpoints.service';
import { catchError, finalize, map, Observable, of } from 'rxjs';
import { AsyncPipe, DatePipe } from '@angular/common';
import { DeviceDto } from '../../org-model/device-dto.interface';
import { SnackbarService } from '../../../../services/snackbar/snackbar.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ButtonComponent } from '../../../../common/components/button/button.component';
import { MatList } from '@angular/material/list';
import { NgxSkeletonLoaderComponent } from 'ngx-skeleton-loader';
import { OrgDeviceDetailsTestIds } from './device-details.test-ids';

@Component({
  selector: 'app-device-details',
  imports: [AsyncPipe, DatePipe, ButtonComponent, MatList, NgxSkeletonLoaderComponent],
  templateUrl: './device-details.component.html',
  styleUrl: './device-details.component.scss'
})
/**
 * Die DeviceDetailsComponent beinhaltet Methoden zum Löschen von Geräten
 * (Devices) aus der Liste und zum Aktualisieren dieser Liste.
 *
 * Umgesetzte AFO: A_26395, Verwaltung der Devices
 */
export class DeviceDetailsComponent implements OnInit {
  domain = input.required<string>();
  userId = input.required<string>();
  devices$: Observable<DeviceDto[]> = of([]);
  isLoading = false;
  skeletonData = new Array(3).fill({});
  protected readonly testIds = OrgDeviceDetailsTestIds;
  private readonly orgAdminEndpointsService = inject(OrgAdminEndpointsService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);

  /**
   * Löscht alle Devices aus der Liste.
   * @param devices die geladenen Devices
   */
  deleteAllDevices(devices: DeviceDto[] | null): void {
    if (!devices || devices.length === 0) {
      return;
    }

    this.isLoading = true;

    this.orgAdminEndpointsService
      .deleteAllDevices(
        this.domain(),
        this.userId(),
        devices.map(device => device.device_id)
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ deleted, failedIds }) => {
          if (failedIds.length === 0) {
            this.snackbarService.success(`${deleted} von ${devices.length} Devices erfolgreich gelöscht`);
          } else {
            // Eine Sammelmeldung statt einzelner Snackbars: MatSnackBar zeigt nur
            // eine gleichzeitig, Einzelmeldungen würden sich gegenseitig überschreiben.
            this.snackbarService.error(
              `${deleted} von ${devices.length} Devices gelöscht, ${failedIds.length} fehlgeschlagen: ${failedIds.join(', ')}`
            );
          }
          this.loadDevices();
        },
        error: () => {
          this.isLoading = false;
          this.snackbarService.error(
            'Beim Löschen der Devices ist ein Fehler aufgetreten. Versuchen Sie es später erneut'
          );
        }
      });
  }
  ngOnInit(): void {
    effect(
      () => {
        this.loadDevices();
      },
      { injector: this.injector }
    );
  }

  protected deleteDevice(device: DeviceDto): void {
    this.isLoading = true;
    this.orgAdminEndpointsService
      .deleteDevice(this.domain(), this.userId(), device.device_id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: () => {
          this.isLoading = false;
          this.snackbarService.error(
            'Beim Löschen der Devices ist ein Fehler aufgetreten. Versuchen Sie es später erneut'
          );
        },
        complete: () => {
          this.loadDevices();
          this.snackbarService.success('Das Device wurde erfolgreich gelöscht');
        }
      });
  }

  private loadDevices(): void {
    this.isLoading = true;
    this.devices$ = this.orgAdminEndpointsService.getDevicesFromUser(this.domain(), this.userId()).pipe(
      map(result => {
        return result.result.sort((a, b) => (b.last_seen_ts ?? 0) - (a.last_seen_ts ?? 0));
      }),
      catchError(() => {
        this.snackbarService.error('Es konnten keine Devices geladen werden');
        throw new Error('Could not load Devices');
      }),
      finalize(() => {
        this.isLoading = false;
      })
    );
  }
}
